import { Store, Truck } from "lucide-react";

/**
 * Availability badge — either "In store now" (green) or "Arrives in X days" (navy).
 * Drives the two states from Section 3 of the website spec:
 *   A. AVAILABLE FOR PICKUP TODAY — stock > 0 AND fulfillment_type = "in_stock"
 *   B. ORDER NOW - ARRIVES IN X DAYS — fulfillment_type = "order_in"
 *
 * Never render a generic "in stock" or "backorder" label.
 */
export function AvailabilityBadge({ product, size = "sm", className = "" }) {
  if (!product) return null;
  const isSpecial = product.fulfillment_type === "order_in";
  const inStock = !isSpecial && (product.stock ?? 0) > 0;

  if (product.ships_only) {
    return (
      <div
        className={`inline-flex items-center gap-1 bg-[#0A1C40] text-[#FFC220] ${size === "lg" ? "text-xs font-black px-3 py-1.5 tracking-widest uppercase" : "text-[10px] font-black px-2 py-1 tracking-widest uppercase"} ${className}`}
        data-testid="badge-ships-to-you"
      >
        <Truck className={size === "lg" ? "w-3.5 h-3.5" : "w-3 h-3"} />
        Ships to you
      </div>
    );
  }

  const base =
    size === "lg"
      ? "text-xs font-black px-3 py-1.5 tracking-widest uppercase"
      : "text-[10px] font-black px-2 py-1 tracking-widest uppercase";

  if (inStock) {
    return (
      <div
        className={`inline-flex items-center gap-1 bg-[#15803d] text-white ${base} ${className}`}
        data-testid="badge-pickup-today"
      >
        <Store className={size === "lg" ? "w-3.5 h-3.5" : "w-3 h-3"} />
        Available in store right now
      </div>
    );
  }

  if (isSpecial) {
    const days = product.lead_time_days || 7;
    return (
      <div
        className={`inline-flex items-center gap-1 bg-[#0A1C40] text-[#FFC220] ${base} ${className}`}
        data-testid="badge-arrives-in-days"
        data-lead-time-days={days}
      >
        <Truck className={size === "lg" ? "w-3.5 h-3.5" : "w-3 h-3"} />
        Arrives in {days} days
      </div>
    );
  }

  // Out of stock non-special-order — soft "sold out" state
  return (
    <div
      className={`inline-flex items-center gap-1 bg-gray-200 text-gray-700 ${base} ${className}`}
      data-testid="badge-sold-out"
    >
      Sold out
    </div>
  );
}
